// Polyline helpers for routes: sampling, curves and positions along a path.
import type { Island, Point, Route } from './island';

/** Splits each segment so that consecutive points are at most `step` apart. */
export function resample(pts: readonly Point[], step = 0.05): Point[] {
  const out: Point[] = [];
  for (let i = 0; i < pts.length - 1; i++) {
    const [x1, z1] = pts[i] as Point;
    const [x2, z2] = pts[i + 1] as Point;
    const n = Math.max(1, Math.ceil(Math.hypot(x2 - x1, z2 - z1) / step));
    for (let k = 0; k < n; k++) out.push([x1 + ((x2 - x1) * k) / n, z1 + ((z2 - z1) * k) / n]);
  }
  const last = pts[pts.length - 1];
  if (last) out.push(last);
  return out;
}

/** Samples a quadratic Bézier curve into `n + 1` points. */
export function quadratic(p0: Point, p1: Point, p2: Point, n = 40): Point[] {
  const out: Point[] = [];
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const a = (1 - t) ** 2;
    const b = 2 * (1 - t) * t;
    const c = t * t;
    out.push([a * p0[0] + b * p1[0] + c * p2[0], a * p0[1] + b * p1[1] + c * p2[1]]);
  }
  return out;
}

/** A polyline with the distance from its start to each point, measured once. */
export interface MeasuredPath {
  readonly pts: readonly Point[];
  readonly distances: Float64Array;
  readonly length: number;
}

export function measurePath(pts: readonly Point[]): MeasuredPath {
  if (pts.length === 0) throw new Error('El camino está vacío');
  const distances = new Float64Array(pts.length);
  for (let i = 1; i < pts.length; i++) {
    const [x1, z1] = pts[i - 1] as Point;
    const [x2, z2] = pts[i] as Point;
    distances[i] = (distances[i - 1] as number) + Math.hypot(x2 - x1, z2 - z1);
  }
  return { pts, distances, length: distances[pts.length - 1] as number };
}

export interface PathPose {
  x: number;
  z: number;
  /** Heading around the vertical axis, as `atan2(dx, dz)`. */
  angle: number;
}

/** Position and heading at fraction `t` (0 to 1) of the path length, written into `out`. */
export function along(path: MeasuredPath, t: number, out: PathPose = { x: 0, z: 0, angle: 0 }): PathPose {
  const { pts, distances } = path;
  const first = pts[0] as Point;
  if (pts.length === 1) {
    out.x = first[0];
    out.z = first[1];
    out.angle = 0;
    return out;
  }
  const d = path.length * Math.min(1, Math.max(0, t));
  // First segment whose end is at or beyond d; the last one takes whatever is left.
  let lo = 0;
  let hi = pts.length - 2;
  while (lo < hi) {
    const mid = (lo + hi) >> 1;
    if (d <= (distances[mid + 1] as number)) hi = mid;
    else lo = mid + 1;
  }
  const [x1, z1] = pts[lo] as Point;
  const [x2, z2] = pts[lo + 1] as Point;
  const len = (distances[lo + 1] as number) - (distances[lo] as number);
  const k = len ? Math.min(1, (d - (distances[lo] as number)) / len) : 0;
  out.x = x1 + (x2 - x1) * k;
  out.z = z1 + (z2 - z1) * k;
  out.angle = Math.atan2(x2 - x1, z2 - z1);
  return out;
}

/** World-space points of a route (grid units shifted so the island is centred on the origin). */
export function routePath(island: Island, route: Route): Point[] {
  const toWorld = ([x, z]: Point): Point => [x - island.cols / 2, z - island.rows / 2];
  if (route.pts) return resample(route.pts.map(toWorld));
  if (route.curve) {
    const [a, b, c] = route.curve;
    return quadratic(toWorld(a), toWorld(b), toWorld(c));
  }
  throw new Error(`La ruta ${route.name} no tiene camino`);
}

/** Ping-pong phase: goes 0 to 1 and back to 0 every `2 * duration` seconds. */
export function pingPong(seconds: number, duration: number, offset = 0): number {
  const cycle = (((seconds / duration + offset) % 2) + 2) % 2;
  return cycle <= 1 ? cycle : 2 - cycle;
}
